import React from 'react';
import Modal from 'AWS-UI-Components-React/polaris/modal';
import Button from '@amzn/awsui-components-react/polaris/button';
import FormField from '@amzn/awsui-components-react-v3/polaris/form-field';
import SpaceBetween from '@amzn/awsui-components-react-v3/polaris/space-between';
import DatePicker from '@amzn/awsui-components-react-v3/polaris/date-picker';
import RadioGroup from '@amzn/awsui-components-react-v3/polaris/radio-group';

/**
 * Converts YYYY-MM format to YYYYMM format.
 * @param {string} datePickerValue - Date in YYYY-MM format (e.g., "2026-02")
 * @returns {string|null} Date in YYYYMM format (e.g., "202602")
 */
export function datePickerToYYYYMM(datePickerValue) {
  if (!datePickerValue) return null;
  return datePickerValue.replace('-', '');
}

/**
 * Converts YYYYMM format to YYYY-MM format for DatePicker.
 * @param {string} yyyymmDateString - Date in YYYYMM format (e.g., "202602")
 * @returns {string} Date in YYYY-MM format (e.g., "2026-02")
 */
export function yyyymmToDatePicker(yyyymmDateString) {
  if (!yyyymmDateString || yyyymmDateString.length !== 6) return '';
  return `${yyyymmDateString.substring(0, 4)}-${yyyymmDateString.substring(4, 6)}`;
}

/**
 * Calculates the next month given a YYYYMM string.
 * Handles year boundary (e.g., 202312 -> 202401).
 * @param {string} yyyymmDateString - Month in YYYYMM format (e.g., "202312")
 * @returns {string} Next month in YYYYMM format (e.g., "202401")
 */
export function calculateNextMonth(yyyymmDateString) {
  const year = parseInt(yyyymmDateString.substring(0, 4), 10);
  const month = parseInt(yyyymmDateString.substring(4, 6), 10) - 1; // Convert to 0-indexed
  const date = new Date(year, month + 1); // Advance by one month; Date handles year overflow
  return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}`;
}

/**
 * ScheduleMappingModal - A modal component for scheduling a new mapping that terminates the current active mapping.
 *
 * This modal allows users to:
 * - Select when the current ongoing mapping should end (month picker)
 * - Choose whether to copy configuration from current mapping or start blank
 * - Auto-calculates the new mapping's start date as the month following the selected end month
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {Function} props.onCancel - Callback function when user cancels
 * @param {Function} props.onContinue - Callback function when user clicks Continue, receives scheduling data
 * @param {string} props.component - The component name for display purposes
 */
export class ScheduleMappingModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedEndMonth: null,
      copyFromCurrent: true
    };
    this.handleCancel = this.handleCancel.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleEndMonthChange = this.handleEndMonthChange.bind(this);
    this.handleCopyOptionChange = this.handleCopyOptionChange.bind(this);
  }

  /**
   * Handles cancel button click.
   * Resets state and calls parent onCancel callback.
   */
  handleCancel() {
    this.setState({
      selectedEndMonth: null,
      copyFromCurrent: true
    });
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  }

  /**
   * Handles continue button click.
   * Passes scheduling data to parent onContinue callback.
   * Includes: selectedEndMonth (YYYYMM), calculatedStartMonth (YYYYMM), copyFromCurrent (boolean)
   */
  handleContinue() {
    const { selectedEndMonth, copyFromCurrent } = this.state;
    
    // Convert selectedEndMonth from YYYY-MM to YYYYMM format
    const endMonthYYYYMM = datePickerToYYYYMM(selectedEndMonth);
    
    // Calculate the start month for the new mapping (month after end month)
    const calculatedStartMonth = endMonthYYYYMM ? calculateNextMonth(endMonthYYYYMM) : null;
    
    if (this.props.onContinue) {
      this.props.onContinue({
        selectedEndMonth: endMonthYYYYMM,
        calculatedStartMonth,
        copyFromCurrent
      });
    }
  }

  /**
   * Handles end month selection change.
   * @param {Object} detail - The change detail from AWSUI DatePicker containing the value
   */
  handleEndMonthChange(detail) {
    this.setState({ selectedEndMonth: detail.value });
  }

  /**
   * Handles copy option radio button change.
   * @param {boolean} copyFromCurrent - Whether to copy from current mapping
   */
  handleCopyOptionChange(copyFromCurrent) {
    this.setState({ copyFromCurrent });
  }

  /**
   * Checks if a date is enabled (current month or future).
   * @param {Date} date - The date to check
   * @returns {boolean} True if the date is current month or later
   */
  isDateEnabled(date) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    
    // Allow current month and future months
    if (dateYear > currentYear) return true;
    return dateYear === currentYear && dateMonth >= currentMonth;

  }

  render() {
    const { visible, component } = this.props;
    const { selectedEndMonth } = this.state;
    
    // Calculate the new mapping's start month if an end month is selected
    const calculatedStartMonth = selectedEndMonth 
      ? calculateNextMonth(datePickerToYYYYMM(selectedEndMonth))
      : null;

    return (
      <Modal
        visible={visible}
        onDismiss={this.handleCancel}
        header={`Schedule New Mapping - ${component || ''}`}
        footer={
          <span className="awsui-util-f-r">
            <Button text="Cancel" onClick={this.handleCancel} />
            <Button
              variant="primary"
              text="Continue"
              onClick={this.handleContinue}
            />
          </span>
        }
        content={
          <SpaceBetween size="l">
            <FormField
              label="End month for current mapping"
              description="Select the month when the current ongoing mapping should end"
            >
              <DatePicker
                value={selectedEndMonth || ''}
                onChange={({ detail }) => this.handleEndMonthChange(detail)}
                granularity="month"
                placeholder="YYYY-MM"
                isDateEnabled={(date) => this.isDateEnabled(date)}
                i18nStrings={{
                  todayAriaLabel: 'Today',
                  nextMonthAriaLabel: 'Next month',
                  previousMonthAriaLabel: 'Previous month'
                }}
              />
            </FormField>
            
            {calculatedStartMonth && (
              <div>
                New mapping will start from: <strong>{yyyymmToDatePicker(calculatedStartMonth)}</strong>
              </div>
            )}
            
            <FormField
              label="Configuration for new mapping"
              description="Choose how to initialize the new mapping's configuration"
            >
              <RadioGroup
                value={this.state.copyFromCurrent ? 'copy' : 'blank'}
                onChange={({ detail }) => this.handleCopyOptionChange(detail.value === 'copy')}
                items={[
                  {
                    value: 'copy',
                    label: 'Copy configuration from current mapping',
                    description: 'Start with the same benchmarks and CP values as the current mapping'
                  },
                  {
                    value: 'blank',
                    label: 'Start with blank configuration',
                    description: 'Create a new mapping with empty fields'
                  }
                ]}
              />
            </FormField>
          </SpaceBetween>
        }
      />
    );
  }
}

export default ScheduleMappingModal;



// WEBPACK FOOTER //
// ./src/components/mapper/schedule_mapping_modal.jsx